'use client';

import { useState, useRef, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { TranscriptRemoveConfirm } from './transcript-confirm-remove';

export interface TranscriptSpeakerCardProps {
  index: number;
  speaker: string;
  time: string;
  description: string;
  descriptionHtml: string;
  icon: {
    color: string;
    text?: number;
  };
  onRemove: () => void;
  onEditSpeaker: (oldValue: string, newValue: string) => void;
  onEditText: (index: number, newValue: string) => void;
}

export function TranscriptSpeakerCard(props: TranscriptSpeakerCardProps) {
  const { index, description, descriptionHtml, speaker, icon, time, onRemove, onEditSpeaker, onEditText } = props;
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(speaker);
  const editRef = useRef<HTMLInputElement>(null);
  const [isTextEditing, setIsTextEditing] = useState(false);
  const [editTextValue, setEditTextValue] = useState(description);
  const editTextRef = useRef<HTMLTextAreaElement>(null);
  const { toast } = useToast();
  const t = useTranslations('features');

  const copyToBuffer = async () => {
    if (navigator.clipboard) {
      try {
        if (description) {
          await navigator.clipboard.writeText(`${time} ${speaker}: ${description}`);
          toast({ title: t('transcript.copied') });
        }
      } catch (err) {
        console.info('Failed to copy: ', err);
        toast({ title: t('transcript.failed_to_copy'), description: t('transcript.unknown_error') });
      }
    } else {
      toast({ title: t('transcript.failed_to_copy'), description: t('transcript.clipboard_not_available') });
    }
  };

  const handleDelete = () => {
    onRemove();
    setShowConfirmPopup(false);
  };

  const toggleConfirmPopup = () => {
    setShowConfirmPopup(!showConfirmPopup);
  };

  const handleEditSpeaker = () => {
    setIsEditing(true);
  };
  const handleEditText = () => {
    setIsTextEditing(true);
  };

  useEffect(() => {
    if (isEditing) {
      const input = editRef.current;
      input && input.focus();
    }
  }, [isEditing]);

  useEffect(() => {
    if (isTextEditing && editTextRef.current) {
      const element = editTextRef.current;
      const length = element.value.length;
      element.focus();
      element.setSelectionRange(length, length);
    }
  }, [isTextEditing]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Enter') {
        onEditSpeaker(speaker, editValue);
      }
      if (e.key === 'Escape') {
        setEditValue(speaker);
      }
      setIsEditing(false);
    }
  };

  const handleTextKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Enter') {
        onEditText(index, editTextValue);
      }
      if (e.key === 'Escape') {
        setEditTextValue(description);
      }
      setIsTextEditing(false);
    }
  };

  const handleBlur = () => {
    onEditSpeaker(speaker, editValue);
    setIsEditing(false);
  };
  const handleTextBlur = () => {
    onEditText(index, editTextValue);
    setIsTextEditing(false);
  };

  const autoResizeTextarea = () => {
    const textarea = editTextRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };
  useEffect(() => {
    if (isTextEditing) {
      autoResizeTextarea();
    }
  }, [editTextValue, isTextEditing]);

  return (
    <div
      data-ref={`transcript-speaker-card-${index}`}
      className={classNames(
        'group text-[13px] mb-[8px] pl-[12px] pr-[15px] pt-[16px] pb-[15px] border border-transparent rounded-[6px] bg-transparent transition-bg duration-250',
        isEditing || isTextEditing ? 'border-[#0E7490]' : 'hover:border-neutral-200 hover:bg-neutral-100'
      )}
      style={isEditing || isTextEditing ? { border: '1px solid #0E7490' } : {}}
    >
      <div className="flex flex-row items-center mb-[8px] font-medium">
        <div className="flex grow items-center text-black">
          <div
            className={classNames(
              'rounded-full w-[20px] h-[20px]  flex items-center justify-center mr-[8px]',
              `bg-${icon.color}-50`
            )}
          >
            <Icon
              icon={IconsMap.USER_FILL_3}
              className={classNames('w-[14px] h-[14px]', `text-${icon.color}-${icon.text ?? 400}`)}
            />
          </div>
          <div
            className="flex h-[20px] items-center relative"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {isEditing ? (
              <input
                ref={editRef}
                type="text"
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                className="input-edit-class !outline-none !border-none"
              />
            ) : (
              <div>{speaker}</div>
            )}
            {isHovering && !isEditing && (
              <div className="relative top-[2px] transition-opacity opacity-0 duration-250 group-hover:opacity-100">
                <button className="ml-[8px]" onClick={handleEditSpeaker}>
                  <Icon icon={IconsMap.EDIT} className="text-neutral-500 w-[16px] h-[16px] hover:text-cyan-900" />
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="text-cyan-900">{time}</div>
      </div>
      {isTextEditing ? (
        <textarea
          ref={editTextRef}
          value={editTextValue}
          onChange={(e) => {
            setEditTextValue(e.target.value);
            autoResizeTextarea();
          }}
          onBlur={handleTextBlur}
          onKeyDown={handleTextKeyDown}
          className="textarea resize-none overflow-hidden input-edit-class w-full h-full leading-[18.46px] !outline-none !border-none"
        />
      ) : (
        <div className="leading-[18.46px]" dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
      )}
      <div className="flex items-center justify-end transition-opacity opacity-0 duration-250 group-hover:opacity-100">
        <button className="ml-[8px]" onClick={copyToBuffer}>
          <Icon icon={IconsMap.COPY} className="text-neutral-500 w-[16px] h-[16px] hover:text-cyan-900" />
        </button>
        <button className="ml-[8px]" onClick={handleEditText}>
          <Icon icon={IconsMap.EDIT} className="text-neutral-500 w-[16px] h-[16px] hover:text-cyan-900" />
        </button>
        <button className="ml-[8px]" onClick={toggleConfirmPopup}>
          <Icon icon={IconsMap.TRASH} className="text-neutral-500 w-[16px] h-[16px] hover:text-cyan-900" />
        </button>
      </div>
      <div className="relative">
        <TranscriptRemoveConfirm
          isVisible={showConfirmPopup}
          title={t('transcript.delete_line_confirmation')}
          onConfirm={handleDelete}
          onCancel={() => setShowConfirmPopup(false)}
        />
      </div>
    </div>
  );
}
